import React, { useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import SetPasswordPage from '../pages/login/SetPasswordPage';

/**
 * Shown when a recovery or invite link has expired or was already used.
 *
 * Supabase redirects back with an `error` hash (`error_code=otp_expired`,
 * `access_denied`) and no usable token. Without this the user lands on the
 * sign-in page with no explanation, tries the same dead link again, and
 * concludes the site is broken.
 */
function ExpiredLinkPage() {
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 3,
        bgcolor: 'background.default',
      }}
    >
      <Box sx={{ maxWidth: 420 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, mb: 1.5, color: 'primary.main' }}>
          This link has expired
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3, lineHeight: 1.65 }}>
          Sign-in and password links are only good for a short time, and only
          once. Request a fresh one and it’ll work.
        </Typography>
        <Button
          variant="contained"
          onClick={() => {
            window.location.hash = '';
            window.location.assign('/login');
          }}
        >
          Back to sign in
        </Button>
      </Box>
    </Box>
  );
}

/**
 * Catches Supabase auth links wherever they land, before routing runs.
 *
 * This exists because `redirectTo` is only honoured when the exact URL is in
 * Supabase's Redirect URL allowlist. When it isn't, Supabase silently discards
 * it and sends the user to the project's Site URL instead — which is how a
 * password link ended up on http://localhost:3000 on 2026-08-27, unreachable
 * from the phone that opened it.
 *
 * The token travels in the URL hash regardless of which path the user lands on.
 * So rather than depending on server-side config being right, read the hash
 * here and render the set-password screen wherever we are. The /reset-password
 * route still exists for when the allowlist IS configured; this makes the flow
 * work when it isn't.
 *
 * Pattern taken from ardent-scholar's InviteGate.
 *
 * `type=magiclink` is deliberately NOT caught — that's an ordinary sign-in and
 * should fall through to normal routing.
 */
export default function InviteGate({ children }: { children: React.ReactNode }) {
  const hash = window.location.hash;

  const [needsPassword, setNeedsPassword] = useState(
    hash.includes('type=recovery') || hash.includes('type=invite'),
  );

  const linkExpired =
    !needsPassword &&
    hash.includes('error') &&
    (hash.includes('otp_expired') ||
      hash.includes('access_denied') ||
      hash.includes('error_code'));

  if (needsPassword) {
    return <SetPasswordPage onComplete={() => setNeedsPassword(false)} />;
  }
  if (linkExpired) {
    return <ExpiredLinkPage />;
  }
  return <>{children}</>;
}
